import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useBalanceContext } from "../../contexts/BalanceContext";
import { useNotifications } from "../../contexts/NotificationContext";
import { usePlayMode } from "../../contexts/PlayModeContext";
import { PrimaryButton, SecondaryButton } from "../components/buttons";
import { ProfileDropdown } from "../components/navigation";
import UniversalSearch from "../../casino/components/UniversalSearch";
import affiliateService from "../services/affiliateService";
import logger from "../utils/logger";
// Canonical tactile-gold recipe (gradient + inset highlights). Imported so the
// .roost-tactile-gold utility is available regardless of whether a gold Button
// is mounted; Vite dedupes the shared import. See button-gold.css.
import "../../components/ui/button-gold.css";

// Notification Icon Component - Reusable notification bell icon
const NotificationIcon = ({ className = "w-6 h-6" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth="1.5"
    stroke="currentColor"
    className={className}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0"
    />
  </svg>
);

// Back Arrow Icon Component
const BackArrowIcon = ({ className = "w-6 h-6" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth="1.5"
    stroke="currentColor"
    className={className}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18"
    />
  </svg>
);

// Desktop Notification Dropdown Content Component
const NotificationDropdownContent = ({
  notifications,
  navigate,
  setNotificationDropdownOpen,
  handleNotificationHover,
  onMarkAllRead,
  getTimeAgo,
}) => (
  <>
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-lg font-bold text-white">Notifications</h3>
      <button
        className="text-xs text-blue-400 hover:text-blue-300 cursor-pointer"
        onClick={onMarkAllRead}
      >
        Mark all as read
      </button>
    </div>

    <div className="space-y-3 max-h-96 overflow-y-auto">
      {notifications.length > 0 ? (
        notifications.map((notification) => (
          <div
            key={notification.id}
            className={`flex items-start space-x-3 p-3 rounded-lg transition-colors cursor-pointer ${
              !notification.isRead
                ? "bg-gray-600/60 hover:bg-gray-600/80"
                : "bg-card/50 hover:bg-muted"
            }`}
            onClick={() => {
              if (notification.actionUrl) {
                navigate(notification.actionUrl);
              }
              setNotificationDropdownOpen(false);
              handleNotificationHover(notification.id);
            }}
            onMouseLeave={() => handleNotificationHover(notification.id)}
          >
            <div className="w-6 h-6 flex items-center justify-center mt-1 flex-shrink-0">
              {notification.imageUrl ? (
                <img
                  src={notification.imageUrl}
                  alt="Notification"
                  className="w-6 h-6 object-cover rounded"
                  onLoad={(e) => {
                    // Hide fallback icon when image loads successfully
                    const fallback = e.target.nextSibling;
                    if (fallback) fallback.style.display = "none";
                  }}
                  onError={(e) => {
                    // Show fallback icon if image fails to load
                    e.target.style.display = "none";
                    const fallback = e.target.nextSibling;
                    if (fallback) fallback.style.display = "block";
                  }}
                />
              ) : null}
              <span
                className="text-lg"
                style={{ display: notification.imageUrl ? "none" : "block" }}
              >
                {notification.icon || "🔔"}
              </span>
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1">
                <span
                  className={`text-sm ${!notification.isRead ? "font-bold" : "font-semibold"} text-white`}
                >
                  {notification.title}
                </span>
                <div className="flex items-center space-x-1">
                  <span
                    className={`text-xs ${!notification.isRead ? "text-gray-300" : "text-gray-400"}`}
                  >
                    {getTimeAgo(notification.createdAt)}
                  </span>
                  {/* Unread indicator dot next to timestamp */}
                  {!notification.isRead && (
                    <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                  )}
                </div>
              </div>
              <p
                className={`text-sm ${!notification.isRead ? "text-gray-200" : "text-gray-300"}`}
              >
                {notification.message}
              </p>
            </div>
          </div>
        ))
      ) : (
        <div className="text-center py-8">
          <div className="flex justify-center mb-4">
            <NotificationIcon className="w-12 h-12 text-gray-400" />
          </div>
          <p className="text-gray-400 text-sm">No notifications yet</p>
        </div>
      )}
    </div>
  </>
);

/**
 * Persistent Casino Header - Renders once and stays stable
 * No re-rendering during navigation to prevent flickering
 */
const CasinoHeader = () => {
  // Add CSS animation for mobile search dropdown
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      @keyframes fadeIn {
        from {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);
  const { user, isLoggedIn, isAdmin, loading } = useAuth();
  const { balances } = useBalanceContext(); // Removed account type toggle
  const {
    notifications,
    unreadCount,
    shouldAutoOpen,
    clearAutoOpen,
    markAsRead,
    markAllAsRead,
    getTimeAgo,
  } = useNotifications();
  const { playMode: activeCurrency, setPlayMode: setActiveCurrency } =
    usePlayMode();
  const navigate = useNavigate();

  const [balanceDropdownOpen, setBalanceDropdownOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notificationDropdownOpen, setNotificationDropdownOpen] =
    useState(false);
  const [mobileNotificationVisible, setMobileNotificationVisible] =
    useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [isApprovedAffiliate, setIsApprovedAffiliate] = useState(false);
  const userDropdownRef = useRef(null);
  const balanceDropdownRef = useRef(null);
  const notificationDropdownRef = useRef(null);

  // Check affiliate status when logged in (on mount, not when dropdown opens)
  useEffect(() => {
    if (isLoggedIn) {
      affiliateService
        .getStatus()
        .then((status) => {
          setIsApprovedAffiliate(
            status.isAffiliate && status.affiliateStatus === "approved",
          );
        })
        .catch(() => setIsApprovedAffiliate(false));
    } else {
      setIsApprovedAffiliate(false);
    }
  }, [isLoggedIn]);

  // Compact balance formatter (matches sidebar: K/M with one decimal)
  const formatCompact = (amount, decimalsForSmall = 0) => {
    if (amount == null || isNaN(amount)) return "0";
    if (amount >= 1000000000) return (amount / 1000000000).toFixed(1) + "B";
    if (amount >= 1000000) return (amount / 1000000).toFixed(1) + "M";
    if (amount >= 1000) return (amount / 1000).toFixed(1) + "K";
    return amount.toLocaleString("en-US", {
      minimumFractionDigits: decimalsForSmall,
      maximumFractionDigits: decimalsForSmall,
    });
  };
  const formatGold = (amount) => formatCompact(amount, 0);
  const formatCB = (amount) => formatCompact(amount, 2);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        userDropdownRef.current &&
        !userDropdownRef.current.contains(event.target)
      ) {
        setUserMenuOpen(false);
      }
      if (
        balanceDropdownRef.current &&
        !balanceDropdownRef.current.contains(event.target)
      ) {
        setBalanceDropdownOpen(false);
      }
      if (
        notificationDropdownRef.current &&
        !notificationDropdownRef.current.contains(event.target)
      ) {
        setNotificationDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Lock body scroll when mobile notification panel is open
  useEffect(() => {
    if (mobileNotificationVisible) {
      // Lock scroll on mobile only
      const isMobile = window.innerWidth < 768; // md breakpoint
      if (isMobile) {
        document.body.style.overflow = "hidden";
      }
    } else {
      // Restore scroll
      document.body.style.overflow = "unset";
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [mobileNotificationVisible]);

  // Auto-open notification dropdown when new notifications arrive
  useEffect(() => {
    if (shouldAutoOpen && !notificationDropdownOpen && isLoggedIn) {
      setNotificationDropdownOpen(true);
      clearAutoOpen(); // Clear the auto-open flag
    }
  }, [shouldAutoOpen, notificationDropdownOpen, isLoggedIn, clearAutoOpen]);

  // Mark notification as read when clicked
  const handleNotificationHover = async (notificationId) => {
    try {
      const notification = notifications.find((n) => n.id === notificationId);
      if (notification && !notification.isRead) {
        await markAsRead(notificationId, "clicked");

        // Context will handle updating the state via real-time updates
      }
    } catch (error) {
      logger.error("Failed to mark notification as read:", error);
    }
  };

  // Mark all notifications as read
  const handleMarkAllRead = async () => {
    try {
      await markAllAsRead();

      // Context will handle updating the state via real-time updates
    } catch (error) {
      logger.error("Failed to mark all notifications as read:", error);
    }
  };

  return (
    <header className="sticky top-0 h-16 bg-card flex items-center px-4 z-[100]">
      {/* Left side - Logo for mobile */}
      <div className="flex items-center md:hidden">
        <img
          src="/Logo-symbol.png"
          alt="Cluck Casino"
          className="w-8 h-8 object-contain cursor-pointer"
          onClick={() => navigate("/")}
        />
      </div>

      {/* Mobile spacer to center search icon */}
      <div className="flex-1 md:hidden"></div>

      {/* Center - Search bar (on all pages) - centered on desktop */}
      <div className="hidden md:flex flex-1 min-w-0 mx-4 justify-center">
        <UniversalSearch />
      </div>

      {/* Mobile Search Toggle Button - Centered */}
      <div className="md:hidden flex items-center justify-center">
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setMobileSearchOpen(!mobileSearchOpen);
          }}
          className={`p-2 transition-colors ${mobileSearchOpen ? "text-white" : "text-gray-400 hover:text-white"}`}
        >
          <Search className="w-5 h-5" />
        </button>
      </div>

      {/* Mobile spacer to center search icon between logo and right elements */}
      <div className="flex-1 md:hidden"></div>

      {/* Right side - User controls */}
      <div className="flex items-center space-x-4 flex-shrink-0">
        {isLoggedIn ? (
          <>
            {/* Balance Display */}
            <div className="flex items-center space-x-4">
              {/* Balance Display - Crypto Only */}
              <div className="relative" ref={balanceDropdownRef}>
                <button
                  onClick={() => setBalanceDropdownOpen(!balanceDropdownOpen)}
                  className="flex items-center space-x-2 md:space-x-3 bg-card rounded-lg px-2 md:px-4 py-2 cursor-pointer transition-all duration-200 hover:bg-gray-600 min-h-11"
                >
                  <img
                    src={
                      activeCurrency === "gold"
                        ? "/assets/coins/Gold-Coin.png"
                        : "/assets/coins/Cluck-Coin.png"
                    }
                    alt={activeCurrency === "gold" ? "Gold Coin" : "Cluck Coin"}
                    className="w-6 h-6 md:w-8 md:h-8 object-contain flex-shrink-0"
                  />
                  <div className="flex flex-col items-start min-w-0">
                    <span className="hidden">
                      {activeCurrency === "gold" ? "Gold Coins" : "Cluck Bucks"}
                    </span>
                    <span
                      className={`font-bold text-sm md:text-lg truncate ${
                        activeCurrency === "gold"
                          ? "text-primary"
                          : "text-green-400"
                      }`}
                    >
                      {activeCurrency === "gold"
                        ? `${formatGold(balances.gold)} GLD`
                        : `${formatCB(balances.swp)} CB`}
                    </span>
                  </div>
                  <svg
                    className={`w-4 h-4 transition-transform duration-200 flex-shrink-0 ${
                      balanceDropdownOpen ? "rotate-180" : ""
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>

                {/* Dropdown Menu */}
                {balanceDropdownOpen && (
                  <>
                    {/* Mobile Full-Width Dropdown */}
                    <div className="md:hidden fixed left-0 right-0 top-16 bg-card border-b border-border shadow-lg z-50">
                      <div className="p-3">
                        <div className="text-xs text-gray-400 uppercase tracking-wide mb-3">
                          Default Play Mode:
                        </div>

                        {/* Gold Balance Option */}
                        <button
                          onClick={() => {
                            setActiveCurrency("gold");
                            setBalanceDropdownOpen(false);
                          }}
                          className={`w-full flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors mb-2 ${
                            activeCurrency === "gold"
                              ? "bg-yellow-600/20 border border-yellow-600/30"
                              : "hover:bg-muted border border-transparent"
                          }`}
                        >
                          <div className="flex items-center space-x-3">
                            <img
                              src="/assets/coins/Gold-Coin.png"
                              alt="Gold Coin"
                              className="w-8 h-8 object-contain"
                            />
                            <div className="flex flex-col items-start">
                              <span className="text-xs text-gray-400">
                                Gold Coins
                              </span>
                              <span className="text-sm font-bold text-primary">
                                GLD
                              </span>
                            </div>
                          </div>
                          <span className="text-lg font-bold text-primary">
                            {formatGold(balances.gold)}
                          </span>
                        </button>

                        {/* Cluck Bucks Balance Option */}
                        <button
                          onClick={() => {
                            setActiveCurrency("swp");
                            setBalanceDropdownOpen(false);
                          }}
                          className={`w-full flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${
                            activeCurrency === "swp"
                              ? "bg-green-600/20 border border-green-600/30"
                              : "hover:bg-muted border border-transparent"
                          }`}
                        >
                          <div className="flex items-center space-x-3">
                            <img
                              src="/assets/coins/Cluck-Coin.png"
                              alt="Cluck Coin"
                              className="w-8 h-8 object-contain"
                            />
                            <div className="flex flex-col items-start">
                              <span className="text-xs text-gray-400">
                                Cluck Bucks
                              </span>
                              <span className="text-sm font-bold text-green-400">
                                CB
                              </span>
                            </div>
                          </div>
                          <span className="text-lg font-bold text-green-400">
                            {formatCB(balances.swp)}
                          </span>
                        </button>
                      </div>
                    </div>

                    {/* Desktop Dropdown - Original */}
                    <div className="hidden md:block absolute right-0 mt-2 w-64 bg-card border border-border rounded-lg shadow-lg z-50">
                      <div className="p-3">
                        <div className="text-xs text-gray-400 uppercase tracking-wide mb-3">
                          Default Play Mode:
                        </div>

                        {/* Gold Balance Option */}
                        <button
                          onClick={() => {
                            setActiveCurrency("gold");
                            setBalanceDropdownOpen(false);
                          }}
                          className={`w-full flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors mb-2 ${
                            activeCurrency === "gold"
                              ? "bg-yellow-600/20 border border-yellow-600/30"
                              : "hover:bg-muted border border-transparent"
                          }`}
                        >
                          <div className="flex items-center space-x-3">
                            <img
                              src="/assets/coins/Gold-Coin.png"
                              alt="Gold Coin"
                              className="w-8 h-8 object-contain"
                            />
                            <div className="flex flex-col items-start">
                              <span className="text-xs text-gray-400">
                                Gold Coins
                              </span>
                              <span className="text-sm font-bold text-primary">
                                GLD
                              </span>
                            </div>
                          </div>
                          <span className="text-lg font-bold text-primary">
                            {formatGold(balances.gold)}
                          </span>
                        </button>

                        {/* Cluck Bucks Balance Option */}
                        <button
                          onClick={() => {
                            setActiveCurrency("swp");
                            setBalanceDropdownOpen(false);
                          }}
                          className={`w-full flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${
                            activeCurrency === "swp"
                              ? "bg-green-600/20 border border-green-600/30"
                              : "hover:bg-muted border border-transparent"
                          }`}
                        >
                          <div className="flex items-center space-x-3">
                            <img
                              src="/assets/coins/Cluck-Coin.png"
                              alt="Cluck Coin"
                              className="w-8 h-8 object-contain"
                            />
                            <div className="flex flex-col items-start">
                              <span className="text-xs text-gray-400">
                                Cluck Bucks
                              </span>
                              <span className="text-sm font-bold text-green-400">
                                CB
                              </span>
                            </div>
                          </div>
                          <span className="text-lg font-bold text-green-400">
                            {formatCB(balances.swp)}
                          </span>
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Notifications */}
              <div className="relative" ref={notificationDropdownRef}>
                <button
                  onClick={() => {
                    const newState = !notificationDropdownOpen;
                    setNotificationDropdownOpen(newState);
                    setMobileNotificationVisible(newState);
                    if (newState) {
                      clearAutoOpen(); // Clear auto-open flag when manually opened
                    }
                  }}
                  className="relative p-2 text-gray-400 hover:text-white transition-colors cursor-pointer"
                >
                  <NotificationIcon />

                  {/* Pulsing red dot indicator - only show when there are unread notifications */}
                  {unreadCount > 0 && (
                    <span className="absolute top-0 right-0 flex h-3 w-3">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-300 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-red-400"></span>
                    </span>
                  )}
                </button>

                {/* Desktop Dropdown */}
                {notificationDropdownOpen && (
                  <div className="hidden md:block absolute right-0 mt-2 w-80 bg-card border border-border rounded-lg shadow-lg z-50">
                    <div className="p-4">
                      <NotificationDropdownContent
                        notifications={notifications}
                        navigate={navigate}
                        setNotificationDropdownOpen={
                          setNotificationDropdownOpen
                        }
                        handleNotificationHover={handleNotificationHover}
                        onMarkAllRead={handleMarkAllRead}
                        getTimeAgo={getTimeAgo}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* User Menu — three elements: gold avatar, single name, chevron.
                Avatar reuses the BIG WIN badge recipe from SocialProofCard
                (vertical 3-stop gradient + tinted inset highlights + drop
                shadow) so the gold treatment is consistent across the app
                and the dark warm initial passes contrast against the mid
                gradient stop. ADMIN pill only renders for admins; everyone
                else is just a name + avatar. Mobile collapses to avatar +
                chevron via `hidden md:inline` on the label/pill. */}
            <div className="relative" ref={userDropdownRef}>
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="group flex items-center gap-2 text-white hover:text-gray-300 transition-colors cursor-pointer"
              >
                {/* Polished-gold avatar - uses the canonical .roost-tactile-gold
                    recipe (see button-gold.css) so it never drifts from the
                    gold button / BIG WIN badge. */}
                <div className="roost-tactile-gold w-8 h-8 rounded-lg flex items-center justify-center transition-transform group-hover:scale-[1.02]">
                  <span className="text-primary-foreground text-sm font-bold">
                    {user?.user_metadata?.first_name?.[0] ||
                      user?.username?.charAt(0)?.toUpperCase() ||
                      user?.email?.[0]?.toUpperCase() ||
                      "U"}
                  </span>
                </div>
                <span className="hidden md:inline text-sm font-medium text-white">
                  {user?.user_metadata?.first_name ||
                    user?.username ||
                    "Player"}
                </span>
                {isAdmin && (
                  <span className="hidden md:inline text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/10 text-white/80">
                    ADMIN
                  </span>
                )}
                <svg
                  className={`w-3.5 h-3.5 text-white/60 transition-transform duration-200 ${
                    userMenuOpen ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {/* User Dropdown */}
              {userMenuOpen && (
                <>
                  {/* Mobile Dropdown */}
                  <div className="md:hidden">
                    <ProfileDropdown
                      isMobile={true}
                      onClose={() => setUserMenuOpen(false)}
                      isApprovedAffiliate={isApprovedAffiliate}
                    />
                  </div>

                  {/* Desktop Dropdown */}
                  <div className="hidden md:block">
                    <ProfileDropdown
                      isMobile={false}
                      onClose={() => setUserMenuOpen(false)}
                      isApprovedAffiliate={isApprovedAffiliate}
                    />
                  </div>
                </>
              )}
            </div>
          </>
        ) : loading ? (
          /* Loading state - Show placeholder to prevent flickering */
          <div className="flex items-center space-x-2">
            <div className="w-16 h-8 bg-card rounded animate-pulse"></div>
            <div className="w-20 h-8 bg-card rounded animate-pulse"></div>
          </div>
        ) : (
          /* Not logged in - Show sign in/up buttons */
          <div className="flex items-center space-x-2 whitespace-nowrap">
            <SecondaryButton onClick={() => navigate("/signin")} size="small">
              Sign In
            </SecondaryButton>
            <PrimaryButton onClick={() => navigate("/signup")} size="small">
              Sign Up
            </PrimaryButton>
          </div>
        )}
      </div>

      {/* Mobile Full-Screen Notification Panel */}
      <>
        {/* Backdrop */}
        <div
          className={`md:hidden fixed inset-0 bg-black z-40 transition-opacity duration-300 ease-in-out ${
            mobileNotificationVisible
              ? "opacity-50 pointer-events-auto"
              : "opacity-0 pointer-events-none"
          }`}
          onClick={() => {
            setNotificationDropdownOpen(false);
            setMobileNotificationVisible(false);
          }}
        />

        {/* Sliding Panel */}
        <div
          className={`md:hidden fixed top-0 right-0 bottom-0 w-full bg-background z-50 transform transition-transform duration-300 ease-in-out ${
            mobileNotificationVisible ? "translate-x-0" : "translate-x-full"
          } flex flex-col`}
        >
          {/* Header */}
          <div className="flex items-center p-4 border-b border-border">
            <button
              onClick={() => {
                setNotificationDropdownOpen(false);
                setMobileNotificationVisible(false);
              }}
              className="p-2 text-gray-400 hover:text-white transition-colors"
            >
              <BackArrowIcon />
            </button>
            <h3 className="text-lg font-bold text-white ml-2 flex-1">
              Notifications
            </h3>
            <button
              className="text-sm text-blue-400 hover:text-blue-300 cursor-pointer px-2 py-1"
              onClick={handleMarkAllRead}
            >
              Mark all read
            </button>
          </div>

          {/* Content */}
          <div
            className="flex-1 overflow-y-auto p-4 overscroll-contain"
            style={{ WebkitOverflowScrolling: "touch" }}
          >
            <div className="space-y-3">
              {notifications.length > 0 ? (
                notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`flex items-start space-x-3 p-4 rounded-lg transition-colors cursor-pointer ${
                      !notification.isRead
                        ? "bg-gray-600/60 hover:bg-gray-600/80"
                        : "bg-card/50 hover:bg-muted"
                    }`}
                    onClick={() => {
                      if (notification.actionUrl) {
                        navigate(notification.actionUrl);
                      }
                      setNotificationDropdownOpen(false);
                      setMobileNotificationVisible(false);
                      handleNotificationHover(notification.id);
                    }}
                  >
                    <div className="w-8 h-8 flex items-center justify-center mt-1 flex-shrink-0">
                      {notification.imageUrl ? (
                        <img
                          src={notification.imageUrl}
                          alt="Notification"
                          className="w-8 h-8 object-cover rounded"
                          onLoad={(e) => {
                            // Hide fallback icon when image loads successfully
                            const fallback = e.target.nextSibling;
                            if (fallback) fallback.style.display = "none";
                          }}
                          onError={(e) => {
                            e.target.style.display = "none";
                            const fallback = e.target.nextSibling;
                            if (fallback) fallback.style.display = "block";
                          }}
                        />
                      ) : null}
                      <span
                        className="text-xl"
                        style={{
                          display: notification.imageUrl ? "none" : "block",
                        }}
                      >
                        {notification.icon || "🔔"}
                      </span>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <span
                          className={`text-base ${!notification.isRead ? "font-bold" : "font-semibold"} text-white`}
                        >
                          {notification.title}
                        </span>
                        <div className="flex items-center space-x-2">
                          <span
                            className={`text-sm ${!notification.isRead ? "text-gray-300" : "text-gray-400"}`}
                          >
                            {getTimeAgo(notification.createdAt)}
                          </span>
                          {!notification.isRead && (
                            <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                          )}
                        </div>
                      </div>
                      <p
                        className={`text-sm ${!notification.isRead ? "text-gray-200" : "text-gray-300"}`}
                      >
                        {notification.message}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-16">
                  <div className="flex justify-center mb-6">
                    <NotificationIcon className="w-16 h-16 text-gray-400" />
                  </div>
                  <p className="text-gray-400 text-base">
                    No notifications yet
                  </p>
                  <p className="text-gray-500 text-sm mt-2">
                    We'll notify you when something important happens
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </>

      {/* Mobile Search Dropdown */}
      {mobileSearchOpen && (
        <div
          className="md:hidden absolute top-full left-0 right-0 bg-background p-4 z-[99]"
          style={{
            animation: "fadeIn 200ms ease-out forwards",
          }}
        >
          <UniversalSearch
            autoOpen={true}
            onClose={() => setMobileSearchOpen(false)}
          />
        </div>
      )}
    </header>
  );
};

export default CasinoHeader;
